// CÔNG THỨC OKR / KPI / NĂNG SUẤT — một nơi, hàm thuần, có test (tests/chi-so.test.ts).
// Owner xác nhận 2026-09-28 (hub UseAI, session vtask 2026-09-28):
//   1. % Mục tiêu = TB có trọng số của KR; KR loại chi-so | viec | kpi
//   2. KPI nhập tay theo kỳ, chiều cao/thấp hơn tốt; ≥100% xanh · 80–99% vàng · <80% đỏ
//   3. Năng suất tự tính: việc xong, % đúng hạn, TG xử lý TB, đang giữ, trễ — theo người phụ trách
//   4. Đối tác = nhãn doi-tac:<slug>
//   5. Sức khoẻ OKR so với % thời gian đã trôi của quý: ≥ kỳ vọng −10đ đúng tiến độ,
//      ≥ kỳ vọng −25đ có rủi ro, thấp hơn là chậm

/* ═════════ Kết quả then chốt & Mục tiêu ═════════ */

export type LoaiKR = 'chi-so' | 'viec' | 'kpi';

export interface KetQua {
  id: number;
  so_muc_tieu: number;
  ten: string;
  loai: LoaiKR;
  bat_dau: number;
  dich: number;
  hien_tai: number;
  don_vi: string;
  trong_so: number;
  kpi_id: number | null;
  phu_trach: number | null;
  thu_tu: number;
  cap_nhat_luc: string;
  tao_boi?: number | null;
}

const kep = (x: number) => (Number.isFinite(x) ? Math.min(1, Math.max(0, x)) : 0);

// Tiến độ theo khoảng điểm đầu → đích. Đích nhỏ hơn điểm đầu (vd giảm tỉ lệ lỗi 8% → 2%) vẫn đúng
// vì cả tử lẫn mẫu cùng đổi dấu. Đích = điểm đầu: đạt khi hiện tại đã chạm đích.
export function tienDoKhoang(bat_dau: number, dich: number, hien_tai: number): number {
  if (dich === bat_dau) return hien_tai >= dich ? 1 : 0;
  return kep((hien_tai - bat_dau) / (dich - bat_dau));
}

// Tiến độ một KR, 0..1. `viec` = {xong, tong} việc con của mục tiêu; `gia_tri_kpi` = giá trị kỳ gần nhất.
export function tienDoKR(kr: Pick<KetQua, 'loai' | 'bat_dau' | 'dich' | 'hien_tai'>, viec?: { xong: number; tong: number } | null, gia_tri_kpi?: number | null): number {
  if (kr.loai === 'viec') return viec && viec.tong > 0 ? kep(viec.xong / viec.tong) : 0;
  if (kr.loai === 'kpi') return gia_tri_kpi === null || gia_tri_kpi === undefined ? 0 : tienDoKhoang(kr.bat_dau, kr.dich, gia_tri_kpi);
  return tienDoKhoang(kr.bat_dau, kr.dich, kr.hien_tai);
}

// % Mục tiêu (0..1) = Σ(tiến độ × trọng số) / Σ trọng số. Không có KR → null (giao diện dùng % việc con).
export function tienDoMucTieu(ds: { tien_do: number; trong_so: number }[]): number | null {
  const tong = ds.reduce((s, k) => s + (k.trong_so > 0 ? k.trong_so : 0), 0);
  if (!ds.length || tong <= 0) return null;
  return kep(ds.reduce((s, k) => s + k.tien_do * (k.trong_so > 0 ? k.trong_so : 0), 0) / tong);
}

/* ═════════ Chu kỳ quý & sức khoẻ ═════════ */

export const maQuy = (d: Date) => `${d.getFullYear()}-q${Math.floor(d.getMonth() / 3) + 1}`;

export function khoangQuy(ma: string): [Date, Date] | null {
  const m = ma.match(/^(\d{4})-q([1-4])$/);
  if (!m) return null;
  const nam = Number(m[1]);
  const q = Number(m[2]);
  return [new Date(nam, (q - 1) * 3, 1), new Date(nam, q * 3, 1)]; // [đầu quý, đầu quý sau)
}

export const tenQuy = (ma: string) => {
  const m = ma.match(/^(\d{4})-q([1-4])$/);
  return m ? `Q${m[2]}/${m[1]}` : ma;
};

export function quyLanCan(ma: string, buoc: number): string {
  const k = khoangQuy(ma);
  if (!k) return ma;
  const d = new Date(k[0]);
  d.setMonth(d.getMonth() + buoc * 3);
  return maQuy(d);
}

export type SucKhoe = 'dung' | 'rui-ro' | 'cham' | 'xong' | 'chua-bat-dau';

// Kỳ vọng = % thời gian đã trôi của quý. Thực tế ≥ kỳ vọng −10đ → đúng tiến độ; ≥ −25đ → có rủi ro; còn lại chậm.
export function sucKhoe(tien_do: number, ma_quy: string | null, bay_gio = new Date()): { muc: SucKhoe; ky_vong: number | null } {
  if (tien_do >= 1) return { muc: 'xong', ky_vong: null };
  const k = ma_quy ? khoangQuy(ma_quy) : null;
  if (!k) return { muc: tien_do > 0 ? 'dung' : 'chua-bat-dau', ky_vong: null };
  const [dau, cuoi] = k;
  const ky_vong = kep((bay_gio.getTime() - dau.getTime()) / (cuoi.getTime() - dau.getTime()));
  if (ky_vong <= 0) return { muc: 'chua-bat-dau', ky_vong: 0 };
  const lech = tien_do - ky_vong;
  return { muc: lech >= -0.1 ? 'dung' : lech >= -0.25 ? 'rui-ro' : 'cham', ky_vong };
}

/* ═════════ Kỳ KPI (tuần ISO / tháng) ═════════ */

export type KieuKy = 'tuan' | 'thang';

// Tuần ISO-8601: tuần bắt đầu thứ Hai; tuần 1 là tuần chứa thứ Năm đầu tiên của năm.
function tuanIso(d0: Date): { nam: number; tuan: number } {
  const d = new Date(Date.UTC(d0.getFullYear(), d0.getMonth(), d0.getDate()));
  const thu = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - thu);
  const dau_nam = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return { nam: d.getUTCFullYear(), tuan: Math.ceil(((d.getTime() - dau_nam.getTime()) / 86400000 + 1) / 7) };
}

export function maKy(d: Date, kieu: KieuKy): string {
  if (kieu === 'thang') return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  const { nam, tuan } = tuanIso(d);
  return `${nam}-W${String(tuan).padStart(2, '0')}`;
}

// [đầu kỳ, đầu kỳ sau) theo giờ địa phương.
export function khoangKy(ma: string): [Date, Date] | null {
  let m = ma.match(/^(\d{4})-(\d{2})$/);
  if (m) return [new Date(Number(m[1]), Number(m[2]) - 1, 1), new Date(Number(m[1]), Number(m[2]), 1)];
  m = ma.match(/^(\d{4})-W(\d{2})$/);
  if (!m) return null;
  const nam = Number(m[1]);
  const tuan = Number(m[2]);
  const ngay4 = new Date(nam, 0, 4); // 4/1 luôn thuộc tuần 1
  const thu_hai_tuan1 = new Date(nam, 0, 4 - ((ngay4.getDay() || 7) - 1));
  const dau = new Date(thu_hai_tuan1);
  dau.setDate(dau.getDate() + (tuan - 1) * 7);
  const cuoi = new Date(dau);
  cuoi.setDate(cuoi.getDate() + 7);
  return [dau, cuoi];
}

// n kỳ gần nhất, CŨ → MỚI, kỳ cuối là kỳ chứa `bay_gio`.
export function dsKy(kieu: KieuKy, n: number, bay_gio = new Date()): string[] {
  const kq: string[] = [];
  const d = new Date(bay_gio.getFullYear(), bay_gio.getMonth(), kieu === 'thang' ? 1 : bay_gio.getDate());
  for (let i = 0; i < n; i++) {
    kq.unshift(maKy(d, kieu));
    if (kieu === 'thang') d.setMonth(d.getMonth() - 1);
    else d.setDate(d.getDate() - 7);
  }
  return kq;
}

export function tenKy(ma: string): string {
  let m = ma.match(/^(\d{4})-(\d{2})$/);
  if (m) return `T${Number(m[2])}/${m[1]}`;
  m = ma.match(/^(\d{4})-W(\d{2})$/);
  if (!m) return ma;
  const k = khoangKy(ma)!;
  const f = (d: Date) => `${d.getDate()}/${d.getMonth() + 1}`;
  const cuoi = new Date(k[1]);
  cuoi.setDate(cuoi.getDate() - 1);
  return `Tuần ${Number(m[2])} (${f(k[0])}–${f(cuoi)})`;
}

// Mã kỳ NHẬP được: có thật (mã → khoảng → mã ra lại chính nó: tuần 53 chỉ ở năm có 53 tuần), từ năm 2000,
// và không quá kỳ SAU kỳ hiện tại (60 dòng "2099-xx" từng đẩy mất lịch sử thật khỏi GET /api/kpi).
export function kyHopLe(ma: unknown, kieu: KieuKy, bay_gio = new Date()): boolean {
  if (typeof ma !== 'string') return false;
  if (kieu === 'thang' ? !/^\d{4}-(0[1-9]|1[0-2])$/.test(ma) : !/^\d{4}-W(0[1-9]|[1-4]\d|5[0-3])$/.test(ma)) return false;
  if (Number(ma.slice(0, 4)) < 2000) return false;
  const k = khoangKy(ma);
  if (!k || maKy(k[0], kieu) !== ma) return false;
  const tran = new Date(bay_gio);
  if (kieu === 'thang') tran.setMonth(tran.getMonth() + 1, 1); else tran.setDate(tran.getDate() + 7);
  return ma <= maKy(tran, kieu);
}

/* ═════════ Mức đạt KPI ═════════ */

export type MucDat = 'xanh' | 'vang' | 'do';

// Tỉ lệ đạt: chiều tăng = giá trị / chỉ tiêu; chiều giảm = chỉ tiêu / giá trị (thấp hơn = tốt hơn).
export function tiLeDat(gia_tri: number | null, chi_tieu: number | null, chieu: 'tang' | 'giam'): number | null {
  if (gia_tri === null || chi_tieu === null || !Number.isFinite(gia_tri) || !Number.isFinite(chi_tieu)) return null;
  if (chieu === 'tang') return chi_tieu === 0 ? (gia_tri >= 0 ? 1 : 0) : gia_tri / chi_tieu;
  if (gia_tri <= 0) return gia_tri <= chi_tieu ? 1 : 0; // 0 lỗi khi chỉ tiêu ≤ 2 lỗi = đạt trọn
  return chi_tieu / gia_tri;
}

export function mucDat(ti_le: number | null): MucDat | null {
  if (ti_le === null) return null;
  return ti_le >= 1 ? 'xanh' : ti_le >= 0.8 ? 'vang' : 'do';
}

/* ═════════ Năng suất tự tính từ dữ liệu việc ═════════ */

export type CongThuc = 'viec-xong' | 'dung-han' | 'thoi-gian-xu-ly' | 'dang-giu' | 'tre-han';

export const TEN_CONG_THUC: Record<CongThuc, { ten: string; don_vi: string; chieu: 'tang' | 'giam'; chi_ky_hien_tai?: boolean }> = {
  'viec-xong': { ten: 'Số việc xong', don_vi: 'việc', chieu: 'tang' },
  'dung-han': { ten: '% xong đúng hạn', don_vi: '%', chieu: 'tang' },
  'thoi-gian-xu-ly': { ten: 'Thời gian xử lý TB', don_vi: 'ngày', chieu: 'giam' },
  'dang-giu': { ten: 'Việc đang giữ', don_vi: 'việc', chieu: 'giam', chi_ky_hien_tai: true },
  'tre-han': { ten: 'Việc trễ hạn', don_vi: 'việc', chieu: 'giam', chi_ky_hien_tai: true },
};

export interface ViecThongKe {
  so: number;
  mo: boolean;
  tao_luc: string;
  dong_luc: string | null;
  han: string | null; // YYYY-MM-DD
  nguoi: number[];
  doi_tac: string[];
  brand: string | null;
  tags: string[];
  la_muc_tieu: boolean;
}

export interface PhamVi { nguoi?: number; doi_tac?: string; brand?: string; tag?: string }

// KPI "liên quan" một việc: phạm vi KHÔNG rỗng và việc nằm trong phạm vi đó. KPI toàn công ty (phạm vi rỗng) khớp mọi
// việc nên không tính — hiện ra ở mọi việc là nhiễu.
export function kpiKhopViec(p: PhamVi | null | undefined, v: ViecThongKe): boolean {
  if (!p || !(p.nguoi !== undefined || p.doi_tac || p.brand || p.tag)) return false;
  return trongPhamVi(v, p);
}

export function trongPhamVi(v: ViecThongKe, p: PhamVi): boolean {
  if (v.la_muc_tieu) return false;
  if (p.nguoi !== undefined && !v.nguoi.includes(p.nguoi)) return false;
  if (p.doi_tac && !v.doi_tac.includes(p.doi_tac)) return false;
  if (p.brand && v.brand !== p.brand) return false;
  if (p.tag && !v.tags.includes(p.tag)) return false;
  return true;
}

const ngayCuaIso = (iso: string) => new Date(iso).toLocaleDateString('sv-SE');

// Đúng hạn = ngày đóng (giờ địa phương) ≤ ngày hạn. Làm tròn theo NGÀY: đóng 23:59 ngày hạn vẫn đúng hạn.
export const dongDungHan = (v: Pick<ViecThongKe, 'dong_luc' | 'han'>) => !!v.dong_luc && !!v.han && ngayCuaIso(v.dong_luc) <= v.han;

// Việc xong tính cho người phụ trách LÚC ĐÓNG (owner chốt 28/09).
// Bảng phụ trách chỉ giữ hiện tại; nhật ký giữ mọi lần đổi kèm danh sách "trước khi đổi" (`tu`), xếp theo thời gian.
// → Người lúc đóng = `tu` của lần đổi ĐẦU TIÊN sau lúc đóng. Không có lần đổi nào sau đó thì là người hiện tại.
// Không có bước này: giao lại việc đã xong (vd người nghỉ việc, admin chuyển hết việc cũ) thì thành tích chuyển sang
// người mới, lịch sử của người cũ biến mất.
export interface LanDoiPhuTrach { luc: string; tu: number[] }
export function nguoiLucDong(dong_luc: string | null, hien_tai: number[], doi: LanDoiPhuTrach[] | undefined): number[] {
  if (!dong_luc || !doi?.length) return hien_tai;
  const t = Date.parse(dong_luc); // so sánh bằng số: "…:00Z" và "…:00.123Z" so chuỗi sẽ sai trong cùng một giây
  const sau = doi.find((d) => Date.parse(d.luc) > t);
  return sau ? sau.tu : hien_tai;
}

// Giá trị một công thức trong kỳ [dau, cuoi). null = không đo được (vd % đúng hạn khi không việc nào có hạn,
// hoặc "đang giữ" cho kỳ đã qua — trạng thái quá khứ không dựng lại được từ issue).
export function tinhCongThuc(ct: CongThuc, ds: ViecThongKe[], p: PhamVi, dau: Date, cuoi: Date, bay_gio = new Date()): number | null {
  const tv = ds.filter((v) => trongPhamVi(v, p));
  const dong_trong_ky = tv.filter((v) => !v.mo && v.dong_luc && new Date(v.dong_luc) >= dau && new Date(v.dong_luc) < cuoi);
  switch (ct) {
    case 'viec-xong':
      return dong_trong_ky.length;
    case 'dung-han': {
      const co_han = dong_trong_ky.filter((v) => v.han);
      return co_han.length ? Math.round((co_han.filter(dongDungHan).length / co_han.length) * 1000) / 10 : null;
    }
    case 'thoi-gian-xu-ly': {
      if (!dong_trong_ky.length) return null;
      const ngay = dong_trong_ky.map((v) => (new Date(v.dong_luc!).getTime() - new Date(v.tao_luc).getTime()) / 86400000);
      return Math.round((ngay.reduce((a, b) => a + b, 0) / ngay.length) * 10) / 10;
    }
    case 'dang-giu':
    case 'tre-han': {
      if (!(bay_gio >= dau && bay_gio < cuoi)) return null;
      const hom_nay = bay_gio.toLocaleDateString('sv-SE');
      const mo = tv.filter((v) => v.mo);
      return ct === 'dang-giu' ? mo.length : mo.filter((v) => v.han && v.han < hom_nay).length;
    }
  }
}
